import { isDefined, isNonEmpty } from '@sgme/fp';
import { useReadonlyField } from 'components/common-form/contexts/readonly/FieldMetadataContext';
import { ValueWithUnit } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/fields/FixedUnitField';
import { UnderlyingBadgesList } from 'components/common-form/sidePanel/info/UnderlyingBadge';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { LearnMoreIcon } from 'components/shared/LearnMore/LearnMoreIcon';
import { ListGroupItem } from 'components/shared/ListGroup/ListGroupItem';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import { IntlKey } from 'locales';
import { ReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import { PropsWithChildren, ReactNode } from 'react';
import { camelCaseToWords } from 'utils/string';

type FieldWrapperProps = { field: string } & PropsWithChildren;
function FieldWrapper({ field, children }: FieldWrapperProps) {
  return (
    <ListGroupItem
      key={field}
      labelKey={`quoteDetails.${field}` as IntlKey}
      defaultLabelMessage={camelCaseToWords(field)}
      className="ps-0 pe-3 align-items-center"
      e2e={field}
    >
      {children}
    </ListGroupItem>
  );
}
type Props = {
  metadatas: Readonly<Record<string, Readonly<ReadonlyValueDefinition>>>;
};

export function DetailBuilder({ metadatas }: Props) {
  const { getMetadataWithData } = useReadonlyField();
  const copyToClipboard = useCopyTextToClipboard('moreMenu.copyToClipboard.success', 'moreMenu.copyToClipboard.error');

  return Object.values(metadatas).map(metadata => {
    let value: ReactNode | undefined;
    if (!isDefined(metadata) || metadata?.isHidden) {
      return undefined;
    }
    switch (metadata.type) {
      case 'Product':
        const product = getMetadataWithData(metadata);
        value = isDefined(product) && (
          <div className="d-flex gap-1" data-e2e="DocumentationTitle-value">
            <FormattedMessage
              id={`field.${product.productSubtype}` as IntlKey}
              defaultMessage={product.productSubtype}
            />
            <LearnMoreIcon productType={product.productType} productSubType={product.productSubtype} />
          </div>
        );
        break;
      case 'Sensitivity':
        const sensitivity = getMetadataWithData(metadata);
        value = isDefined(sensitivity) && (
          <span data-e2e="SolvedParamSensi-value">
            <FormattedNumber value={sensitivity.value} maximumFractionDigits={4} />
            {' UF for 1% '}
            <FormattedMessage id={`field.${sensitivity.solveFor}` as IntlKey} defaultMessage={sensitivity.solveFor} />
          </span>
        );
        break;
      case 'TextWithPostValue':
        const textWithPostValue = getMetadataWithData(metadata);
        value =
          isDefined(textWithPostValue?.value) && isDefined(textWithPostValue?.postValue) ? (
            <>
              <FormattedMessage
                id={`field.${textWithPostValue.value}` as IntlKey}
                defaultMessage={textWithPostValue.value}
              />
              {' ('}
              <FormattedMessage
                id={`productSummary.postValue.${textWithPostValue.postValue}` as IntlKey}
                defaultMessage={camelCaseToWords(textWithPostValue.postValue)}
              />
              {')'}
            </>
          ) : undefined;
        break;
      case 'Underlyings':
        const underlyings = getMetadataWithData(metadata);
        value = isDefined(underlyings) && <UnderlyingBadgesList underlyings={underlyings} />;
        break;
      case 'Date':
        const date = getMetadataWithData(metadata);
        value = isDefined(date) && <FormattedDate value={date} year="numeric" month="short" day="2-digit" />;
        break;
      case 'Amount':
        const amount = getMetadataWithData(metadata);
        value = isDefined(amount?.value) && isDefined(amount?.unit) && (
          <>
            <FormattedNumber maximumFractionDigits={4} value={amount?.value} /> {amount?.unit}
          </>
        );
        break;
      case 'Number':
        const number = getMetadataWithData(metadata);
        value = isDefined(number) && (
          <span data-e2e={metadata.id + '-value'}>
            <FormattedNumber maximumFractionDigits={4} value={number} />
          </span>
        );
        break;
      case 'FixedUnit':
        const fixedUnit = getMetadataWithData(metadata);
        value = isDefined(fixedUnit) && isDefined(metadata?.unit) && (
          <span data-e2e={metadata.id + '-value'}>
            <ValueWithUnit value={fixedUnit} unit={metadata.unit} />
          </span>
        );
        break;
      case 'Text':
      case 'SolvingMode':
        const text = getMetadataWithData(metadata);
        const translations = metadata?.translations;
        value = isNonEmpty(text) && (
          <>
            {translations && isDefined(text) && translations[text] && translations[text] !== '' ? (
              <FormattedMessage id={`form.${translations[text]}` as IntlKey} defaultMessage={translations[text]} />
            ) : (
              <FormattedMessage id={`field.${text}` as IntlKey} defaultMessage={text} />
            )}
            {metadata?.hasCopyButton && (
              <button type="button" className="btn btn-link btn-sm ms-1" onClick={() => copyToClipboard(text)}>
                <i className="icon icon-xs">content_copy</i>
              </button>
            )}
          </>
        );
        break;
      case 'Client':
        const client = getMetadataWithData(metadata);
        value = isNonEmpty(client?.label) ? (
          <span className="fw-bold" data-e2e={metadata.id + '-value'} title={client.label}>
            {client.label}
          </span>
        ) : undefined;
        break;
      default:
        value = undefined;
    }
    if (value) {
      return (
        <FieldWrapper key={metadata.id} field={metadata.id}>
          {value}
        </FieldWrapper>
      );
    }
    return undefined;
  });
}
